/**
 * Response Strategies Hooks
 * Jotai atoms and hooks for response strategy management
 */

import { useMemo } from 'react';
import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { responseStrategyService } from '@/services';
import { invalidateQueries, queryClient } from '@/lib/query-client';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import type {
	CreateResponseStrategyDto,
	UpdateResponseStrategyDto,
	ResponseStrategy,
	GenerateResponseStrategyResult
} from '@/types';

type ResponseStrategyListFilters = {
	intent?: string;
	status?: 'active' | 'draft' | 'disabled';
};

// Query atoms
const responseStrategiesQueryAtom = (filters?: ResponseStrategyListFilters) =>
	atomWithQuery((get) => ({
		queryKey: ['response-strategies', filters ?? {}, buScopeKey(get)],
		queryFn: () => responseStrategyService.getResponseStrategies(filters, buScopeParams(get)),
		staleTime: 30 * 1000
	}));

const responseStrategyByIdQueryAtom = (id: string) =>
	atomWithQuery(() => ({
		queryKey: ['response-strategies', id],
		queryFn: () => responseStrategyService.getResponseStrategyById(id),
		enabled: !!id,
		staleTime: 30 * 1000
	}));

// Mutation atoms
const createResponseStrategyMutationAtom = atomWithMutation((get) => ({
	mutationKey: ['response-strategies', 'create'],
	mutationFn: (data: CreateResponseStrategyDto) =>
		responseStrategyService.createResponseStrategy(data, buScopeParams(get)),
	onSuccess: () => {
		invalidateQueries(['response-strategies']);
	}
}));

const updateResponseStrategyMutationAtom = atomWithMutation(() => ({
	mutationKey: ['response-strategies', 'update'],
	mutationFn: ({ id, data }: { id: string; data: UpdateResponseStrategyDto }) =>
		responseStrategyService.updateResponseStrategy(id, data),
	onSuccess: (_data, variables) => {
		invalidateQueries(['response-strategies']);
		invalidateQueries(['response-strategies', variables.id]);
	}
}));

const deleteResponseStrategyMutationAtom = atomWithMutation(() => ({
	mutationKey: ['response-strategies', 'delete'],
	mutationFn: (id: string) => responseStrategyService.deleteResponseStrategy(id),
	onSuccess: () => {
		invalidateQueries(['response-strategies']);
	}
}));

function patchResponseStrategyInCaches(id: string, patch: Partial<ResponseStrategy>) {
	queryClient.setQueriesData<{ strategies: ResponseStrategy[]; count: number } | undefined>(
		{ queryKey: ['response-strategies'] },
		(prev) => {
			if (!prev || !Array.isArray(prev.strategies)) return prev;
			let changed = false;
			const next = prev.strategies.map((s) => {
				if (s.id !== id) return s;
				changed = true;
				return { ...s, ...patch };
			});
			return changed ? { ...prev, strategies: next } : prev;
		}
	);
	queryClient.setQueryData<ResponseStrategy | undefined>(['response-strategies', id], (prev) =>
		prev ? { ...prev, ...patch } : prev
	);
}

const setActiveMutationAtom = atomWithMutation(() => ({
	mutationKey: ['response-strategies', 'set-active'],
	mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
		responseStrategyService.setResponseStrategyActive(id, isActive),
	onMutate: async ({ id, isActive }: { id: string; isActive: boolean }) => {
		await queryClient.cancelQueries({ queryKey: ['response-strategies'] });
		const snapshots = queryClient.getQueriesData({ queryKey: ['response-strategies'] });
		patchResponseStrategyInCaches(id, isActive ? { isActive: true, isDraft: false } : { isActive: false });
		return { snapshots };
	},
	onError: (_err, _vars, context: { snapshots: [readonly unknown[], unknown][] } | undefined) => {
		if (context?.snapshots) {
			for (const [key, data] of context.snapshots) {
				queryClient.setQueryData(key, data);
			}
		}
	},
	onSettled: (_data, _error, { id }: { id: string; isActive: boolean }) => {
		invalidateQueries(['response-strategies']);
		invalidateQueries(['response-strategies', id]);
	}
}));

// `/generate` never persists — no cache to invalidate, purely returns a value for review.
const generateResponseStrategyMutationAtom = atomWithMutation(() => ({
	mutationKey: ['response-strategies', 'generate'],
	mutationFn: (input: { intents: string[]; merchantAsk: string }): Promise<GenerateResponseStrategyResult> =>
		responseStrategyService.generateResponseStrategy(input)
}));

const createResponseStrategyFromTemplateMutationAtom = atomWithMutation((get) => ({
	mutationKey: ['response-strategies', 'from-template'],
	mutationFn: (templateId: string) =>
		responseStrategyService.createResponseStrategyFromTemplate(templateId, buScopeParams(get)),
	onSuccess: () => {
		invalidateQueries(['response-strategies']);
	}
}));

// Hooks
export function useResponseStrategies(filters?: ResponseStrategyListFilters) {
	const filterKey = JSON.stringify(filters ?? {});
	const atom = useMemo(() => responseStrategiesQueryAtom(filters), [filterKey]);
	const [query] = useAtom(atom);
	return query;
}

export function useResponseStrategy(id: string) {
	const atom = useMemo(() => responseStrategyByIdQueryAtom(id), [id]);
	const [query] = useAtom(atom);
	return query;
}

export function useCreateResponseStrategy() {
	const [mutation] = useAtom(createResponseStrategyMutationAtom);
	return mutation;
}

export function useUpdateResponseStrategy() {
	const [mutation] = useAtom(updateResponseStrategyMutationAtom);
	return mutation;
}

export function useDeleteResponseStrategy() {
	const [mutation] = useAtom(deleteResponseStrategyMutationAtom);
	return mutation;
}

export function useSetResponseStrategyActive() {
	const [mutation] = useAtom(setActiveMutationAtom);
	return mutation;
}

export function useGenerateResponseStrategy() {
	const [mutation] = useAtom(generateResponseStrategyMutationAtom);
	return mutation;
}

export function useCreateResponseStrategyFromTemplate() {
	const [mutation] = useAtom(createResponseStrategyFromTemplateMutationAtom);
	return mutation;
}
